import type { ComponentProps, MouseEvent, Ref } from "react"
import { forwardRef } from "react"
import { Slot } from "@radix-ui/react-slot"
import cn from "@/v2-ui/utils/utils.cn"
import Icon, { type IconProps } from "@/v2-ui/icon"
import { createRipple } from "./button.ripple"
import {
  STACKED_ICON_SIZE,
  DEFAULT_ICON_SIZE,
  getButtonClasses,
  type ButtonAlign,
  type ButtonSize,
  type ButtonVariant,
} from "./button.constants"

export type ButtonProps = ComponentProps<"button"> & {
  /** Renders the (single) child element as the button (e.g. an anchor). */
  asChild?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  /** Icon above the label, Outlook-ribbon style (sheet toolbars). */
  isStacked?: boolean
  /** Forces a square aspect even with a label (rarely needed). */
  isSquare?: boolean
  isLoading?: boolean
  iconSrc?: IconProps["src"]
  iconSize?: IconProps["size"]
  iconPosition?: "left" | "right"
  iconClassName?: string
  /** Opts out of the press ripple. */
  hasRipple?: boolean
  /**
   * How label and icon sit inside a full-width button. `between` is the
   * picker trigger shape (label left, chevron right). A callsite
   * `justify-*` utility can NOT do this: styles.css loads after the
   * utilities, so `.bo-button`'s justify-center wins the cascade.
   */
  align?: ButtonAlign
}

/**
 * The backoffice button — deliberately standalone, NOT a v2-ui wrapper.
 *
 * All styling lives in button.css as plain classes (base, variants, sizes,
 * states): callsite Tailwind utilities always win over single-class CSS by
 * cascade order, so per-element customization needs no !important and no
 * merge tricks — the exact property the v2-ui utility-only styling made
 * impossible. The prop surface mirrors what the app already used when this
 * wrapped v2-ui, so callsites are unaffected.
 */
function Button(props: ButtonProps, ref: Ref<HTMLButtonElement>) {
  const {
    asChild,
    variant = "primary",
    size = "md",
    isStacked,
    isSquare,
    isLoading,
    iconSrc,
    iconSize,
    iconPosition = "left",
    iconClassName,
    hasRipple = true,
    align,
    type,
    className,
    children,
    onMouseDown,
    ...buttonProps
  } = props

  const isIconOnly = Boolean(iconSrc) && children == null
  const classes = cn(
    getButtonClasses({
      variant,
      size,
      isStacked,
      isIconOnly: isIconOnly || Boolean(isSquare),
      align,
    }),
    className,
  )

  const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    if (hasRipple && !buttonProps.disabled) createRipple(event)
    onMouseDown?.(event)
  }

  // Radix Slot requires exactly one element child, so the icon slots are
  // deliberately skipped with asChild — the caller owns the child markup
  // (render your own icon/spinner inside it).
  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={classes}
        onMouseDown={handleMouseDown}
        {...buttonProps}
      >
        {children}
      </Slot>
    )
  }

  const isIconShown = Boolean(iconSrc || isLoading)
  const resolvedIconSize =
    iconSize || (isStacked ? STACKED_ICON_SIZE : DEFAULT_ICON_SIZE)
  const icon = isIconShown ? (
    <Icon
      aria-hidden
      src={iconSrc}
      size={resolvedIconSize}
      loading={isLoading}
      className={iconClassName}
    />
  ) : null

  return (
    <button
      ref={ref}
      // Native `<button>` elements inside a `<form>` default to
      // `type="submit"` — the classic React form footgun where every
      // "Cancel" silently submits. Default to an inert type; explicit
      // `type="submit"` still works.
      type={type ?? "button"}
      className={classes}
      onMouseDown={handleMouseDown}
      // A loading button is busy, not clickable: a second click while the
      // first request is in flight re-submits it (two createManyUsers calls
      // two seconds apart raced each other into a directory conflict on
      // 2026-10-12). `aria-busy` says why to assistive tech.
      aria-busy={isLoading || undefined}
      {...buttonProps}
      disabled={buttonProps.disabled || isLoading}
    >
      {iconPosition === "left" && icon}
      {children}
      {iconPosition === "right" && icon}
    </button>
  )
}

export default forwardRef<HTMLButtonElement, ButtonProps>(Button)
